import type {
    homeFeedQueriesHouseholdQuery as HouseholdQuery,
} from '../relay/__generated__/homeFeedQueriesHouseholdQuery.graphql';
import type {
    homeFeedQueriesInspectionQuery as InspectionQuery,
} from '../relay/__generated__/homeFeedQueriesInspectionQuery.graphql';

import {
    mapInferences,
    mapWidening,
    type HomeFeedInferencesView,
    type HomeFeedWideningView,
} from './home-feed-offers-view';
import {
    mapEngagement,
    mapResultsChanged,
    mapWildcards,
    type HomeFeedEngagementView,
    type HomeFeedWildcardsView,
    type ResultsChangedView,
} from './home-feed-activity-view';

/**
 * Plain view models for the Home feed panel (PRD pv-platform#156) — decoupled from the Relay generated
 * types so the panel is unit-testable without a Relay harness. Later slices add sections to the
 * inspection view.
 */
export interface HomeFeedMemberView {
    accountId: string;
    role: string;
    joinedUtc: string;
}

export interface HomeFeedSearchView {
    searchId: string;
    name: string;
    stage: string;
    isDefault: boolean;
}

export interface HomeFeedHouseholdView {
    vaultId: string;
    homeFeedOn: boolean;
    members: readonly HomeFeedMemberView[];
    searches: readonly HomeFeedSearchView[];
}

export type PreferenceSource = 'STATED' | 'QUESTION' | 'INFERENCE' | 'WIDENING';
export type PreferenceStatus = 'APPLIED' | 'PENDING' | 'DECLINED';

/** One member's Member preference with where it came from and what the household did with it. */
export interface PreferenceItemView {
    kind: string;
    subject: string;
    value: string;
    /** The server's origin enum, or the raw value for one this build doesn't know. */
    source: string;
    householdStatus: string;
    statedAt: string;
    overriddenByAccountId: string | null;
}

/** One member's statement on a point the household has not simply agreed on. */
export interface PreferenceMemberView {
    memberAccountId: string;
    value: string;
    householdStatus: string;
    overriddenByAccountId: string | null;
}

/** A point every live statement on is Applied with one value. A lone statement is held, not agreed. */
export interface PreferenceHeldView {
    kind: string;
    subject: string;
    value: string;
    memberAccountIds: readonly string[];
}

/** A point awaiting a partner's answer (pending) or contested (conflict). */
export interface PreferenceOpenPointView {
    kind: string;
    subject: string;
    /** What the household Search holds now; null when it holds nothing for the point. */
    householdValue: string | null;
    views: readonly PreferenceMemberView[];
}

export interface HomeFeedPreferencesView {
    items: readonly PreferenceItemView[];
    held: readonly PreferenceHeldView[];
    pending: readonly PreferenceOpenPointView[];
    conflicts: readonly PreferenceOpenPointView[];
}

export interface QuestionAnswerView {
    label: string;
    reordered: number;
}

export interface PickedQuestionView {
    key: string;
    /** PRIORITY / KEY_PLACE (ranking) or FIRMNESS / WHAT_KIND. */
    kind: string;
    prompt: string;
    poolSize: number;
    mostReordered: number;
    totalReordered: number;
    answers: readonly QuestionAnswerView[];
    /** How many homes come before the card. */
    afterHomes: number;
    todayPrefersNonRanking: boolean;
}

export interface OtherQuestionView {
    key: string;
    kind: string;
    prompt: string;
    mostReordered: number;
    totalReordered: number;
}

export interface NextQuestionDayView {
    /** The UTC calendar day, `YYYY-MM-DD`. */
    day: string;
    from: string;
    homesBefore: number;
    prefersNonRanking: boolean;
}

export interface LatestAnswerView {
    key: string;
    /** Empty for a priority or key place question: the answer is the member's preference. */
    value: string;
    answeredUtc: string;
}

export interface HomeFeedQuestionsView {
    /** OFFERED or the first blocker; an unknown (future) value is kept as given. */
    todayReason: string;
    todayUntil: string | null;
    scanDays: number;
    snoozedUntil: string | null;
    pickedOnNextQuestionDay: string | null;
    nextDay: NextQuestionDayView | null;
    picked: PickedQuestionView | null;
    others: readonly OtherQuestionView[];
    latestAnswers: readonly LatestAnswerView[];
}

export interface HomeFeedInspectionView {
    memberAccountId: string;
    searchId: string;
    /** ISO-8601 instant the server worked the inspection out for. */
    evaluatedAt: string;
    homeFeedOn: boolean;
    /** Null when the Home feed is off (nothing was evaluated). */
    preferences: HomeFeedPreferencesView | null;
    /** Null when the Home feed is off. */
    questions: HomeFeedQuestionsView | null;
    /** Each of these is null when the Home feed is off. */
    inferences: HomeFeedInferencesView | null;
    widening: HomeFeedWideningView | null;
    wildcards: HomeFeedWildcardsView | null;
    resultsChanged: ResultsChangedView | null;
    engagement: HomeFeedEngagementView | null;
}

/** What one inspection call came to: a snapshot, a vault/Search/member that is not there, or a failure. */
export type HomeFeedInspectionOutcome =
    | { kind: 'ok'; inspection: HomeFeedInspectionView }
    | { kind: 'not-found' }
    /** Another inspection is already running in the admin process; try again. */
    | { kind: 'busy' }
    | { kind: 'error' };

export type HomeFeedHouseholdOutcome =
    | { kind: 'loading' }
    | { kind: 'ok'; household: HomeFeedHouseholdView }
    | { kind: 'not-found' }
    | { kind: 'error' };

export function mapHousehold(
    household: HouseholdQuery['response']['homeFeedHousehold'],
): HomeFeedHouseholdView {
    return {
        vaultId: household.vaultId,
        homeFeedOn: household.homeFeedOn,
        members: household.members.map((member) => ({
            accountId: member.accountId,
            role: member.role,
            joinedUtc: member.joinedUtc,
        })),
        searches: household.searches.map((search) => ({
            searchId: search.searchId,
            name: search.name,
            stage: search.stage,
            isDefault: search.isDefault,
        })),
    };
}

export function mapInspection(
    inspection: InspectionQuery['response']['homeFeedInspection'],
): HomeFeedInspectionView {
    return {
        memberAccountId: inspection.memberAccountId,
        searchId: inspection.searchId,
        evaluatedAt: inspection.evaluatedAt,
        homeFeedOn: inspection.homeFeedOn,
        preferences: mapPreferences(inspection.preferences),
        questions: mapQuestions(inspection.questions),
        inferences: mapInferences(inspection.inferences),
        widening: mapWidening(inspection.widening),
        wildcards: mapWildcards(inspection.wildcards),
        resultsChanged: mapResultsChanged(inspection.resultsChanged),
        engagement: mapEngagement(inspection.engagement),
    };
}

type QuestionsPayload = InspectionQuery['response']['homeFeedInspection']['questions'];

function mapQuestions(questions: QuestionsPayload): HomeFeedQuestionsView | null {
    if (!questions) return null;
    return {
        todayReason: questions.todayReason,
        todayUntil: questions.todayUntil ?? null,
        scanDays: questions.scanDays,
        snoozedUntil: questions.snoozedUntil ?? null,
        pickedOnNextQuestionDay: questions.pickedOnNextQuestionDay ?? null,
        nextDay: questions.nextDay
            ? {
                  day: questions.nextDay.day,
                  from: questions.nextDay.from,
                  homesBefore: questions.nextDay.homesBefore,
                  prefersNonRanking: questions.nextDay.prefersNonRanking,
              }
            : null,
        picked: questions.picked
            ? {
                  key: questions.picked.key,
                  kind: questions.picked.kind,
                  prompt: questions.picked.prompt,
                  poolSize: questions.picked.poolSize,
                  mostReordered: questions.picked.mostReordered,
                  totalReordered: questions.picked.totalReordered,
                  answers: questions.picked.answers.map((a) => ({ label: a.label, reordered: a.reordered })),
                  afterHomes: questions.picked.afterHomes,
                  todayPrefersNonRanking: questions.picked.todayPrefersNonRanking,
              }
            : null,
        others: questions.others.map((o) => ({
            key: o.key,
            kind: o.kind,
            prompt: o.prompt,
            mostReordered: o.mostReordered,
            totalReordered: o.totalReordered,
        })),
        latestAnswers: questions.latestAnswers.map((a) => ({
            key: a.key,
            value: a.value,
            answeredUtc: a.answeredUtc,
        })),
    };
}

type PreferencesPayload = InspectionQuery['response']['homeFeedInspection']['preferences'];
type OpenPointPayload = NonNullable<PreferencesPayload>['household']['pending'][number];

function mapOpenPoint(point: OpenPointPayload): PreferenceOpenPointView {
    return {
        kind: point.kind,
        subject: point.subject,
        householdValue: point.householdValue ?? null,
        views: point.views.map((view) => ({
            memberAccountId: view.memberAccountId,
            value: view.value,
            householdStatus: view.householdStatus,
            overriddenByAccountId: view.overriddenByAccountId ?? null,
        })),
    };
}

function mapPreferences(preferences: PreferencesPayload): HomeFeedPreferencesView | null {
    if (!preferences) return null;
    return {
        items: preferences.items.map((item) => ({
            kind: item.kind,
            subject: item.subject,
            value: item.value,
            source: item.source,
            householdStatus: item.householdStatus,
            statedAt: item.statedAt,
            overriddenByAccountId: item.overriddenByAccountId ?? null,
        })),
        held: preferences.household.held.map((line) => ({
            kind: line.kind,
            subject: line.subject,
            value: line.value,
            memberAccountIds: [...line.memberAccountIds],
        })),
        pending: preferences.household.pending.map(mapOpenPoint),
        conflicts: preferences.household.conflicts.map(mapOpenPoint),
    };
}

/** Operator-facing label for a preference's origin. Unknown (future) values are shown as given. */
export function preferenceSourceLabel(source: string): string {
    switch (source) {
        case 'STATED':
            return 'Stated or agreed by the member';
        case 'QUESTION':
            return 'Answered a Question card';
        case 'INFERENCE':
            return 'Accepted an Inference';
        case 'WIDENING':
            return 'Accepted a Widening offer';
        default:
            return source;
    }
}

/** Operator-facing label for a preference's household status. */
export function preferenceStatusLabel(status: string): string {
    switch (status) {
        case 'APPLIED':
            return 'Applied';
        case 'PENDING':
            return 'Pending';
        case 'DECLINED':
            return 'Declined';
        default:
            return status;
    }
}

/**
 * The member and Search the panel inspects: the operator's pick while it is still in the household,
 * otherwise the household's first of each (the directory's own order). `null` when there is none to pick.
 */
export function resolveSelection(
    household: HomeFeedHouseholdView,
    picked: { memberAccountId: string | null; searchId: string | null },
): { memberAccountId: string | null; searchId: string | null } {
    const member =
        household.members.find((m) => m.accountId === picked.memberAccountId) ?? household.members[0];
    const search = household.searches.find((s) => s.searchId === picked.searchId) ?? household.searches[0];
    return { memberAccountId: member?.accountId ?? null, searchId: search?.searchId ?? null };
}

interface ErrorCarrier {
    extensions?: { code?: unknown } | null;
    message?: unknown;
    source?: { errors?: unknown } | null;
}

function carriesNotFound(error: ErrorCarrier): boolean {
    return error.extensions?.code === 'NOT_FOUND' || /\bNOT_FOUND\b/.test(String(error.message ?? ''));
}

function carriesBusy(error: ErrorCarrier): boolean {
    return error.extensions?.code === 'INSPECTION_BUSY' || /\bINSPECTION_BUSY\b/.test(String(error.message ?? ''));
}

/**
 * `Apis.Admin` surfaces a missing vault / Search / member as a top-level `NOT_FOUND` error; the root
 * field is non-null, so Relay raises it with the payload errors on `error.source`. Anything else (403
 * for the break-glass key, network, 5xx) is a plain failure.
 * `INSPECTION_BUSY` (one inspection at a time in the admin process) is told apart: it is worth retrying.
 */
export function inspectionFailureKind(error: unknown): 'not-found' | 'busy' | 'error' {
    if (error == null || typeof error !== 'object') return 'error';
    const carrier = error as ErrorCarrier;
    const nested = Array.isArray(carrier.source?.errors) ? (carrier.source.errors as ErrorCarrier[]) : [];
    const all = [carrier, ...nested].filter(Boolean);
    if (all.some(carriesNotFound)) return 'not-found';
    return all.some(carriesBusy) ? 'busy' : 'error';
}
